import { useRef, useState } from 'react';

import { useDebounce } from '@iconik-io/shared-lib/src/react/hooks/use_debounce';
import { type DropdownListOption, useAsyncList } from '@iconik-io/uad';

import { INPUT_DEBOUNCE_TIMEOUT } from '../../constants/debounce';

export type FetchFilterOptions = (
  query: string,
) => Promise<DropdownListOption[]>;

export function useSearchableFilterOptions(
  fetchOptions: FetchFilterOptions,
  selectedKeys: string[],
) {
  const [hasAnyOptions, setHasAnyOptions] = useState<boolean | undefined>(
    undefined,
  );

  const list = useAsyncList<DropdownListOption>({
    async load({ filterText, signal }) {
      const items = await fetchOptions(filterText ?? '');

      if ((filterText ?? '') === '') {
        setHasAnyOptions(items.length > 0);
      }

      if (signal.aborted) {
        return { items: [] };
      }

      return { items };
    },
  });

  const debouncedSetFilterText = useDebounce(
    list.setFilterText,
    INPUT_DEBOUNCE_TIMEOUT,
    [],
  );

  const labelRef = useRef<Record<string, string>>({});
  for (const option of list.items) {
    labelRef.current[String(option.id)] = option.label;
  }

  const options = [
    ...selectedKeys
      .filter(
        (id) =>
          !list.items.some((option) => option.id === id) &&
          labelRef.current[id],
      )
      .map((id) => ({ id, label: labelRef.current[id] })),
    ...list.items,
  ];

  return {
    options,
    isLoading: list.isLoading,
    hasAnyOptions,
    onSearchValueChange: debouncedSetFilterText,
  };
}
